/**
 * Predefined order for skill categories in lesson content.
 * Skills are displayed in this specific order for pedagogical reasons.
 */
const SKILL_ORDER = ['kosakata', 'pemahaman', 'menulis & beropini', 'berpikir matematis', 'proses ilmiah'] as const

/**
 * Pre-computed map for efficient skill order lookup.
 * Created once to avoid recreation on every sort operation.
 */
const SKILL_ORDER_MAP = new Map<string, number>(SKILL_ORDER.map((skill, index) => [skill.toLowerCase(), index]))

/**
 * Sentinel value to represent skills not in the predefined order.
 * These skills are sorted alphabetically after the predefined skills.
 */
const UNKNOWN_SKILL_ORDER = Number.MAX_SAFE_INTEGER

/**
 * Sorts an array of skills or skill-containing objects by a predefined skill order.
 * Skills are ordered according to the SKILL_ORDER constant, with any unrecognized
 * skills sorted alphabetically at the end.
 *
 * @param skillWeights - Array of SelectTag objects or objects containing a tag property
 * @returns The same array, sorted in-place
 *
 * @remarks
 * - This function mutates the input array
 * - Skills are compared case-insensitively
 * - Unrecognized skills appear after predefined skills, sorted alphabetically
 * - The function is optimized with a pre-computed order map for O(1) lookups
 *
 * @example
 * // Sort direct tags
 * const tags = [
 *   { name: 'Proses Ilmiah', ... },
 *   { name: 'Kosakata', ... }
 * ]
 * sortSkillWeight(tags)
 * // Result: [{ name: 'Kosakata', ... }, { name: 'Proses Ilmiah', ... }]
 *
 * @example
 * // Sort objects with nested tags
 * const skillWeights = [
 *   { tag: { name: 'Pemahaman', ... }, weight: 10 },
 *   { tag: { name: 'Kosakata', ... }, weight: 5 }
 * ]
 * sortSkillWeight(skillWeights)
 * // Result: [{ tag: { name: 'Kosakata', ... }, ... }, { tag: { name: 'Pemahaman', ... }, ... }]
 */
export function sortSkillWeight<T extends { name: string }[]>(skillWeights: T): T
export function sortSkillWeight<T extends { tag: { name: string } }[]>(skillWeights: T): T
export function sortSkillWeight<T extends { name: string }[] | { tag: { name: string } }[]>(skillWeights: T): T {
  return skillWeights.sort((a, b) => {
    // Extract skill names from either direct tags or nested tag objects
    const skillNameA = 'tag' in a ? a.tag.name : a.name
    const skillNameB = 'tag' in b ? b.tag.name : b.name

    // Get predefined order indices (or sentinel value if not found)
    const orderA = SKILL_ORDER_MAP.get(skillNameA.toLowerCase()) ?? UNKNOWN_SKILL_ORDER
    const orderB = SKILL_ORDER_MAP.get(skillNameB.toLowerCase()) ?? UNKNOWN_SKILL_ORDER

    // If orders differ, sort by order value
    if (orderA !== orderB) {
      return orderA - orderB
    }

    // If both are unknown or both have the same order, sort alphabetically
    // Using localeCompare for proper language-specific string comparison
    return skillNameA.localeCompare(skillNameB, 'id-ID')
  }) as T
}
